import { firstDefined, unsafeDeepCopy } from './utils';

import { camelToSentenceCase } from './string';
import { Audience, AudienceKeyValPair, AudienceValue } from '../types/audience';

function FALLBACK_AUDIENCE_VALUE(): AudienceValue {
  return { visible: true };
}

function _resolveAudience(
  audience: Audience | undefined,
  parentAud: Audience | undefined
): Audience {
  if (audience) return unsafeDeepCopy(audience);
  if (parentAud) return unsafeDeepCopy(parentAud);

  return {
    syncWithTemplate: true,
    zones: {
      all: FALLBACK_AUDIENCE_VALUE(),
    },
  };
}

export function audienceZoneToName(zone: string): string {
  return camelToSentenceCase(zone.split('-')[1] || '');
}

export function toggleAudienceSync(paramAud: Audience | undefined) {
  const audience = _resolveAudience(paramAud, undefined);

  audience.syncWithTemplate = !audience.syncWithTemplate;

  return audience;
}

export function toggleAudienceEndpoint<T extends keyof AudienceValue>(
  key: T,
  zone: string,
  zones: string[],
  paramAudience: Audience | undefined,
  paramParentAud: Audience | undefined
) {
  let parentAud = _resolveAudience(paramParentAud, undefined);
  let audience = _resolveAudience(paramAudience, parentAud);

  const allToggle = !evaluateAudienceTruthyAggregate(key, zones, audience, parentAud);

  const newZones: AudienceKeyValPair = {};

  zones.forEach(z => {
    const ans = evaluateAudienceZone(key, z, audience, parentAud);
    newZones[z] = (audience.zones || {})[z] || FALLBACK_AUDIENCE_VALUE();
    newZones[z][key] = ans;
  });

  if (zone === 'all') {
    Object.keys(newZones).forEach(z => {
      newZones[z][key] = allToggle;
    });
  } else {
    newZones[zone][key] = !newZones[zone][key];
  }

  audience.zones = newZones;
  audience.syncWithTemplate = false;

  return audience;
}

function _evaluateHelper<T extends keyof AudienceValue>(
  fallback: boolean,
  key: T,
  zone: string,
  audience: Audience
): AudienceValue[T] {
  const zones = audience.zones || {};
  return !!firstDefined(fallback, (zones.all || {})[key], (zones[zone] || {})[key]);
}

export function evaluateAudienceZone<T extends keyof AudienceValue>(
  key: T,
  zone: string,
  paramAudience: Audience | undefined,
  paramTemplateAud: Audience | undefined
): AudienceValue[T] {
  const templateAud = _resolveAudience(paramTemplateAud, undefined);
  const audience = _resolveAudience(paramAudience, templateAud);

  const parentVal = _evaluateHelper(FALLBACK_AUDIENCE_VALUE()[key], key, zone, templateAud);
  const sourceVal = _evaluateHelper(parentVal, key, zone, audience);

  return !audience || audience.syncWithTemplate ? parentVal : sourceVal;
}

export function evaluateAudienceTruthyAggregate<T extends keyof AudienceValue>(
  key: T,
  zones: string[],
  audience: Audience | undefined,
  templateAud: Audience | undefined
): boolean | null {
  let trueCount = 0;
  let falseCount = 0;

  zones.forEach(zone => {
    const ans = evaluateAudienceZone(key, zone, audience, templateAud);

    if (ans) trueCount += 1;
    else falseCount += 1;
  });

  if (trueCount > 0 && falseCount === 0) {
    return true;
  } else if (falseCount > 0 && trueCount === 0) {
    return false;
  } else if (zones.length === 0) {
    return true;
  } else {
    return null;
  }
}
